// ExpandedGameWorkspace — full MLB intelligence workspace for /baseball/mlb/game/:gamePk
// Tabs: Overview · Markets · Bets · [Live] · Stats · Matchup · Context · Lines · Evidence · Risk · Signals · Simulation

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  LayoutDashboard,
  Users,
  Cloud,
  Brain,
  CloudRain,
  Wind,
  MapPin,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  FlaskConical,
  BarChart2,
  DollarSign,
  Radio,
  BarChart3,
  Shield,
  LineChart as LineChartIcon,
} from 'lucide-react';
import type {
  MLBGame,
  GameOdds,
  DataQualityReport,
  BetRecommendation,
  LiveGameData,
  MLBLinescore,
  PlayByPlayEvent,
  LineMovementSignal,
  Bet,
  TimestampedOddsSnapshot,
} from '../../types';
import { TeamLogo } from '../common/TeamLogo';
import { Badge } from '../common/Badge';
import { DataQualityBadge } from '../DataQuality/DataQualityBadge';
import { MatchupWorkspace } from './MatchupWorkspace';
import { ParkFactors, type ParkFactorData } from './ParkFactors';
import { WeatherImpact, type WeatherImpactData } from './WeatherImpact';
import { MarketMicrostructure } from './MarketMicrostructure';
import { ConfidenceEvolution } from './ConfidenceEvolution';
import { SignalFeed } from '../LiveBoard/SignalFeed';
import { OddsMovementChart } from '../system/OddsMovementChart';
import { TopPickCard } from '../GameCard/TopPickCard';
import { MarketRow } from '../GameCard/MarketRow';
import type { BullpenData } from './BullpenAnalytics';
import { formatOdds, getBetOutlook, getStatusColor, formatMarket } from '../../utils/format';
import { useAppStore } from '../../store/useAppStore';
import { oddsApi } from '../../api/client';

// ── Tab definitions ───────────────────────────────────────────────────────────

type WorkspaceTab =
  | 'overview'
  | 'markets'
  | 'bets'
  | 'live'
  | 'stats'
  | 'matchup'
  | 'context'
  | 'lines'
  | 'evidence'
  | 'risk'
  | 'signals'
  | 'simulation';

const ALL_TABS: { id: WorkspaceTab; label: string; icon: React.ElementType }[] = [
  { id: 'overview',    label: 'Overview',    icon: LayoutDashboard },
  { id: 'markets',     label: 'Markets',     icon: BarChart2 },
  { id: 'bets',        label: 'Bets',        icon: DollarSign },
  { id: 'live',        label: 'Live',        icon: Radio },
  { id: 'stats',       label: 'Stats',       icon: BarChart3 },
  { id: 'matchup',     label: 'Matchup',     icon: Users },
  { id: 'context',     label: 'Context',     icon: Cloud },
  { id: 'lines',       label: 'Lines',       icon: LineChartIcon },
  { id: 'evidence',    label: 'Evidence',    icon: Brain },
  { id: 'risk',        label: 'Risk',        icon: Shield },
  { id: 'signals',     label: 'Signals',     icon: Zap },
  { id: 'simulation',  label: 'Simulation',  icon: FlaskConical },
];

// ── Ordinal helper ────────────────────────────────────────────────────────────

const ORDINALS: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd' };
function ordinal(n: number) { return ORDINALS[n] ?? `${n}th`; }

// ── Decision chip helper ──────────────────────────────────────────────────────

function getDecisionMeta(rec: BetRecommendation | undefined, isFinal: boolean, isLive = false) {
  if (isFinal) return null;
  if (isLive && rec?.decisionStatus === 'INVALID') return null;
  if (!rec) return null;
  switch (rec.decisionStatus) {
    case 'STRONG_BET':
      return { label: 'STRONG PLAY', cls: 'text-win bg-win/15 border-win/50 font-bold', pulse: true };
    case 'QUALIFIED_BET':
      if (isLive) return null;
      return { label: 'QUALIFIED', cls: 'text-brand-light bg-brand/15 border-brand/40 font-semibold', pulse: false };
    case 'LEAN':
      return { label: 'LEAN', cls: 'text-warn bg-warn/15 border-warn/40 font-semibold', pulse: false };
    case 'INVALID':
      return isFinal ? null : { label: 'BLOCKED', cls: 'text-loss bg-loss/15 border-loss/40 font-semibold', pulse: false };
    default:
      return rec.edge >= 0.02
        ? { label: 'NEAR EDGE', cls: 'text-warn/70 bg-warn/10 border-warn/20 font-medium', pulse: false }
        : null;
  }
}

// ── Shared StatMini ───────────────────────────────────────────────────────────

function StatMini({ label, value, highlight = 'neutral' }: { label: string; value: string; highlight?: 'win' | 'loss' | 'neutral' }) {
  return (
    <div className="bg-surface-2 rounded px-2 py-1.5 text-center">
      <div className="text-[9px] text-gray-600 uppercase tracking-wide">{label}</div>
      <div className={clsx('text-xs font-mono font-bold mt-0.5', highlight === 'win' ? 'text-win' : highlight === 'loss' ? 'text-loss' : 'text-gray-300')}>
        {value}
      </div>
    </div>
  );
}

// ── WorkspaceContext ──────────────────────────────────────────────────────────

interface WorkspaceContext {
  game: MLBGame;
  liveData?: LiveGameData;
  odds?: GameOdds;
  weather?: WeatherImpactData;
  dataQuality: DataQualityReport;
  awayStandings?: { winPct: number; wins: number; losses: number };
  homeStandings?: { winPct: number; wins: number; losses: number };
  lineMovement?: LineMovementSignal;
  parkData?: ParkFactorData;
  awayBullpen?: BullpenData;
  homeBullpen?: BullpenData;
  recommendations: BetRecommendation[];
  analyzing: boolean;
  onAnalyze: () => void;
}

// ── Runner diamond ────────────────────────────────────────────────────────────

function RunnerDiamond({ linescore }: { linescore: MLBLinescore }) {
  const { offenseFirst, offenseSecond, offenseThird } = linescore;
  return (
    <div className="relative w-9 h-9 flex-shrink-0">
      <div className={clsx('runner-base absolute top-0 left-1/2 -translate-x-1/2', offenseSecond ? 'occupied' : 'empty')} style={{ width: 10, height: 10 }} />
      <div className={clsx('runner-base absolute left-0 top-1/2 -translate-y-1/2', offenseThird ? 'occupied' : 'empty')} style={{ width: 10, height: 10 }} />
      <div className={clsx('runner-base absolute right-0 top-1/2 -translate-y-1/2', offenseFirst ? 'occupied' : 'empty')} style={{ width: 10, height: 10 }} />
      <div className="runner-base empty absolute bottom-0 left-1/2 -translate-x-1/2" style={{ width: 10, height: 10 }} />
    </div>
  );
}

// ── Play-by-play feed ─────────────────────────────────────────────────────────

function PlayByPlayFeed({ plays }: { plays: PlayByPlayEvent[] }) {
  let lastKey = '';
  return (
    <div className="space-y-0.5 max-h-60 overflow-y-auto">
      {plays.map((play, i) => {
        const halfLabel = play.halfInning === 'top' ? 'TOP' : 'BOT';
        const inningKey = `${play.halfInning}-${play.inning}`;
        const showHeader = inningKey !== lastKey;
        lastKey = inningKey;
        return (
          <div key={`${play.atBatIndex}-${i}`}>
            {showHeader && (
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider pt-3 pb-1 first:pt-0">
                {halfLabel} {ordinal(play.inning)}
              </div>
            )}
            <div className={clsx('flex items-start gap-2 py-1.5 px-2 rounded text-xs', play.isScoringPlay && 'border-l-2 border-gold/50 bg-gold/5')}>
              <div className="flex-shrink-0 w-4 mt-0.5">
                {!play.isComplete
                  ? <span className="block w-2 h-2 rounded-full bg-win animate-pulse mt-0.5" />
                  : play.isScoringPlay
                    ? <span className="text-gold">★</span>
                    : <span className="text-gray-600">·</span>}
              </div>
              <div className="flex-1 min-w-0">
                {play.event && (
                  <span className={clsx('font-semibold mr-1.5 uppercase text-[10px] tracking-wide', play.isScoringPlay ? 'text-gold' : !play.isComplete ? 'text-win' : 'text-gray-400')}>
                    {play.event}
                  </span>
                )}
                <span className="text-gray-400 leading-snug">{play.description}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({
  ctx,
  bestRec,
  steamSignal,
  placedBetsCount,
}: {
  ctx: WorkspaceContext;
  bestRec: BetRecommendation | undefined;
  steamSignal: { direction: string; magnitudePts: number } | null;
  placedBetsCount: number;
}) {
  const { game, liveData, odds, weather, dataQuality, awayStandings, homeStandings } = ctx;

  const displayGame = liveData ?? game;
  const detailedState = displayGame.status.detailedState ?? '';
  const isNonStandard = /postponed|cancelled|canceled|suspended/i.test(detailedState);
  const isLive = displayGame.status.abstractGameState === 'Live' && !isNonStandard;
  const isFinal = displayGame.status.abstractGameState === 'Final' && !isNonStandard;

  const decisionMeta = getDecisionMeta(bestRec, isFinal, isLive);

  return (
    <div className="space-y-6">
      {/* Scoreboard card */}
      <div className={clsx('card p-6', isLive && 'border-win/30')}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {isLive && <Badge variant="live" size="sm">LIVE · {displayGame.linescore?.currentInningOrdinal} {displayGame.linescore?.inningHalf}</Badge>}
            {isFinal && <Badge variant="neutral" size="sm">FINAL</Badge>}
            {isNonStandard && <Badge variant="warn" size="sm">{detailedState.toUpperCase()}</Badge>}
            {!isLive && !isFinal && !isNonStandard && (
              <Badge variant="info" size="sm">
                {new Date(game.gameTime ?? game.gameDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })}
              </Badge>
            )}
            {decisionMeta && (
              <span className={clsx('inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border', decisionMeta.cls)}>
                {decisionMeta.pulse && <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse" />}
                {decisionMeta.label}
              </span>
            )}
            {steamSignal && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-warn bg-warn/10 border-warn/30">
                <Zap className="w-3 h-3" />
                STEAM {steamSignal.magnitudePts}pt
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {weather && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                {(weather.precipitationProbability ?? 0) > 30 ? <CloudRain className="w-3 h-3 text-info" /> : <Wind className="w-3 h-3" />}
                {weather.temperature}°F · {weather.windSpeed}mph {weather.windDirection}
              </div>
            )}
            {dataQuality && (
              <div className={clsx('text-xs font-medium', dataQuality.overallScore >= 0.75 ? 'text-win' : dataQuality.overallScore >= 0.5 ? 'text-warn' : 'text-loss')}>
                {Math.round(dataQuality.overallScore * 100)}%
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MapPin className="w-3 h-3" />
              {game.venue.name}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 items-center gap-4">
          {/* Away */}
          <div className="text-center">
            <div className="flex justify-center mb-2"><TeamLogo abbrev={game.awayTeam.abbreviation} size="lg" /></div>
            <div className="text-2xl font-bold text-gray-100">{game.awayTeam.name}</div>
            <div className="text-xs text-gray-500 mt-1">
              {awayStandings ? `${awayStandings.wins}-${awayStandings.losses} (${(awayStandings.winPct * 100).toFixed(0)}%)` : 'Away'}
            </div>
            {(() => {
              const name = isLive ? (liveData?.awayPitcher?.fullName ?? game.probablePitchers?.away?.fullName) : game.probablePitchers?.away?.fullName;
              const pc = isLive ? liveData?.awayPitcher?.pitchCount : undefined;
              if (!name) return null;
              return <div className="text-xs text-gray-400 mt-1">{name}{pc != null ? ` (${pc})` : ''}</div>;
            })()}
            {odds?.awayMoneyline && (
              <div className={clsx('text-sm font-mono font-semibold mt-2', odds.awayMoneyline > 0 ? 'text-win' : 'text-gray-300')}>
                {formatOdds(odds.awayMoneyline)}
              </div>
            )}
          </div>

          {/* Score */}
          <div className="text-center">
            {isLive || isFinal ? (
              <div className="flex items-center justify-center gap-4">
                <span className="text-5xl font-bold font-mono">{displayGame.awayScore ?? 0}</span>
                <span className="text-2xl text-gray-600">—</span>
                <span className="text-5xl font-bold font-mono">{displayGame.homeScore ?? 0}</span>
              </div>
            ) : (
              <div className="text-gray-600 text-4xl font-mono">vs</div>
            )}
            {isLive && displayGame.linescore && (() => {
              const lastPlay = liveData?.recentPlays?.[0];
              return (
                <div className="flex flex-col items-center gap-2 mt-3">
                  <div className="flex items-center gap-4">
                    <RunnerDiamond linescore={displayGame.linescore} />
                    <div className="flex items-center gap-3 text-xs font-mono">
                      <div className="flex flex-col items-center"><span className="text-gray-500">B</span><span className="text-gray-200">{displayGame.linescore.balls ?? 0}</span></div>
                      <div className="flex flex-col items-center"><span className="text-gray-500">S</span><span className="text-gray-200">{displayGame.linescore.strikes ?? 0}</span></div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-gray-500">O</span>
                        <div className="flex items-center gap-0.5">
                          {[0, 1, 2].map((i) => (
                            <span key={i} className={clsx('block w-2 h-2 rounded-full', i < (displayGame.linescore?.outs ?? 0) ? 'bg-warn' : 'bg-surface-4')} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {lastPlay && (
                    <div className="text-center max-w-xs">
                      {lastPlay.event && (
                        <div className={clsx('text-[10px] font-semibold uppercase tracking-wide', !lastPlay.isComplete ? 'text-win' : lastPlay.isScoringPlay ? 'text-gold' : 'text-gray-500')}>
                          {!lastPlay.isComplete && <span className="inline-block w-1.5 h-1.5 rounded-full bg-win animate-pulse mr-1 mb-px align-middle" />}
                          {lastPlay.event}
                        </div>
                      )}
                      {lastPlay.description && <div className="text-[10px] text-gray-400 leading-tight line-clamp-2">{lastPlay.description}</div>}
                    </div>
                  )}
                </div>
              );
            })()}
            {odds?.total && <div className="text-xs text-gray-500 mt-2 font-mono">O/U {odds.total}</div>}
          </div>

          {/* Home */}
          <div className="text-center">
            <div className="flex justify-center mb-2"><TeamLogo abbrev={game.homeTeam.abbreviation} size="lg" /></div>
            <div className="text-2xl font-bold text-gray-100">{game.homeTeam.name}</div>
            <div className="text-xs text-gray-500 mt-1">
              {homeStandings ? `${homeStandings.wins}-${homeStandings.losses} (${(homeStandings.winPct * 100).toFixed(0)}%)` : 'Home'}
            </div>
            {(() => {
              const name = isLive ? (liveData?.homePitcher?.fullName ?? game.probablePitchers?.home?.fullName) : game.probablePitchers?.home?.fullName;
              const pc = isLive ? liveData?.homePitcher?.pitchCount : undefined;
              if (!name) return null;
              return <div className="text-xs text-gray-400 mt-1">{name}{pc != null ? ` (${pc})` : ''}</div>;
            })()}
            {odds?.homeMoneyline && (
              <div className={clsx('text-sm font-mono font-semibold mt-2', odds.homeMoneyline > 0 ? 'text-win' : 'text-gray-300')}>
                {formatOdds(odds.homeMoneyline)}
              </div>
            )}
          </div>
        </div>

        {/* Win probability */}
        {liveData?.winProbability && (
          <div className="mt-4 pt-4 border-t border-surface-4">
            <div className="text-xs text-gray-500 mb-2">Win Probability</div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono w-8">{Math.round(liveData.winProbability.away * 100)}%</span>
              <div className="flex-1 h-2 rounded-full overflow-hidden bg-surface-4">
                <div className="h-full bg-brand" style={{ width: `${liveData.winProbability.away * 100}%` }} />
              </div>
              <span className="text-xs font-mono w-8 text-right">{Math.round(liveData.winProbability.home * 100)}%</span>
            </div>
          </div>
        )}

        {/* Placed bets quick link */}
        {placedBetsCount > 0 && (
          <div className="mt-3 pt-3 border-t border-surface-4 text-xs text-brand">
            {placedBetsCount} bet{placedBetsCount !== 1 ? 's' : ''} placed — see Bets tab
          </div>
        )}
      </div>

      {/* Linescore */}
      {game.linescore?.innings && game.linescore.innings.length > 0 && (
        <div className="card p-4 overflow-x-auto">
          <div className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider">Line Score</div>
          <table className="text-xs font-mono w-full min-w-max">
            <thead>
              <tr className="text-gray-600">
                <td className="pr-4 text-left">Team</td>
                {game.linescore.innings.map((inn) => <td key={inn.num} className="w-8 text-center">{inn.num}</td>)}
                <td className="w-8 text-center font-bold text-gray-500 pl-2">R</td>
                <td className="w-8 text-center font-bold text-gray-500">H</td>
                <td className="w-8 text-center font-bold text-gray-500">E</td>
              </tr>
            </thead>
            <tbody>
              {(['away', 'home'] as const).map((side) => {
                const team = side === 'away' ? game.awayTeam : game.homeTeam;
                return (
                  <tr key={side} className="text-gray-300">
                    <td className="pr-4 font-semibold">
                      <div className="flex items-center gap-1.5">
                        <TeamLogo abbrev={team.abbreviation} size="xs" />
                        {team.abbreviation}
                      </div>
                    </td>
                    {game.linescore!.innings!.map((inn) => {
                      const data = inn[side];
                      return <td key={inn.num} className="w-8 text-center text-gray-400">{data?.runs ?? '-'}</td>;
                    })}
                    <td className="w-8 text-center font-bold pl-2">{game.linescore!.innings!.reduce((s, i) => s + (i[side]?.runs ?? 0), 0)}</td>
                    <td className="w-8 text-center text-gray-500">{game.linescore!.innings!.reduce((s, i) => s + (i[side]?.hits ?? 0), 0)}</td>
                    <td className="w-8 text-center text-gray-500">{game.linescore!.innings!.reduce((s, i) => s + (i[side]?.errors ?? 0), 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Play by play */}
      {isLive && liveData?.recentPlays && liveData.recentPlays.length > 0 && (
        <div className="card p-4">
          <div className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider">Play by Play</div>
          <PlayByPlayFeed plays={liveData.recentPlays} />
        </div>
      )}

      <DataQualityBadge quality={dataQuality} showDetails />
    </div>
  );
}

// ── Markets tab ───────────────────────────────────────────────────────────────

function MarketsTab({
  ctx,
  placedBets,
}: {
  ctx: WorkspaceContext;
  placedBets: Bet[];
}) {
  const { game, odds, recommendations, analyzing, onAnalyze } = ctx;
  const gameDate = game.gameDate.substring(0, 10);

  const unplacedRecs = recommendations.filter(
    (r) =>
      r.decisionStatus !== 'INVALID' &&
      !placedBets.some(
        (b) =>
          (b.recommendationId && b.recommendationId === r.id) ||
          (b.market === r.market && b.selection === r.selection),
      ),
  );
  const qualifiedRecs = unplacedRecs.filter(
    (r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET',
  );
  const otherRecs = unplacedRecs.filter(
    (r) => r.decisionStatus !== 'STRONG_BET' && r.decisionStatus !== 'QUALIFIED_BET',
  );

  const recByMarket = new Map<string, BetRecommendation>();
  for (const rec of unplacedRecs) {
    if (!recByMarket.has(rec.market)) recByMarket.set(rec.market, rec);
    else if (rec.edge > recByMarket.get(rec.market)!.edge) recByMarket.set(rec.market, rec);
  }

  const allQualifiedRecByMarket = new Map<string, BetRecommendation>();
  for (const rec of recommendations.filter(
    (r) => r.decisionStatus !== 'INVALID' && (r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET'),
  )) {
    if (!allQualifiedRecByMarket.has(rec.market)) allQualifiedRecByMarket.set(rec.market, rec);
    else if (rec.edge > allQualifiedRecByMarket.get(rec.market)!.edge) allQualifiedRecByMarket.set(rec.market, rec);
  }

  const placedMarkets = new Set<string>();
  for (const [mkt] of allQualifiedRecByMarket) {
    if (!recByMarket.has(mkt)) placedMarkets.add(mkt);
  }

  const awayAbbr = game.awayTeam.abbreviation;
  const homeAbbr = game.homeTeam.abbreviation;

  type MarketRowDef = Parameters<typeof MarketRow>[0];
  const marketRows: (Omit<MarketRowDef, 'game' | 'gameDate' | 'recommendation' | 'isBetPlaced' | 'isQualifiedGame'>)[] = [];

  if (odds?.awayMoneyline != null || odds?.homeMoneyline != null)
    marketRows.push({ market: 'moneyline', label: 'Moneyline', awayLabel: awayAbbr, homeLabel: homeAbbr, awayOdds: odds?.awayMoneyline, homeOdds: odds?.homeMoneyline });

  if (odds?.awayRunline != null || odds?.homeRunline != null) {
    const spread = odds?.runlineSpread ?? 1.5;
    marketRows.push({ market: 'runline', label: 'Run Line', awayLabel: awayAbbr, homeLabel: homeAbbr, awayOdds: odds?.awayRunline, homeOdds: odds?.homeRunline, awaySpreadLabel: `+${spread}`, homeSpreadLabel: `-${spread}` });
  }
  if (odds?.total != null) {
    const tot = odds.total;
    marketRows.push({ market: 'total', label: 'Total', awayLabel: 'OVER', homeLabel: 'UNDER', awayOdds: odds?.overOdds, homeOdds: odds?.underOdds, awaySpreadLabel: Number(tot).toFixed(1), homeSpreadLabel: Number(tot).toFixed(1) });
  }
  if (odds?.firstFiveAwayMoneyline != null || odds?.firstFiveHomeMoneyline != null)
    marketRows.push({ market: 'first5_moneyline', label: 'F5 ML', awayLabel: awayAbbr, homeLabel: homeAbbr, awayOdds: odds?.firstFiveAwayMoneyline, homeOdds: odds?.firstFiveHomeMoneyline });
  if (odds?.firstFiveTotal != null) {
    const tot = odds.firstFiveTotal;
    marketRows.push({ market: 'first5_total', label: 'F5 Total', awayLabel: 'OVER', homeLabel: 'UNDER', awaySpreadLabel: Number(tot).toFixed(1), homeSpreadLabel: Number(tot).toFixed(1) });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {qualifiedRecs.length > 0 && (
            <span className="text-[9px] bg-win/15 text-win border border-win/30 rounded-full px-1.5 py-0.5">
              {qualifiedRecs.length} qualified
            </span>
          )}
        </div>
        <button onClick={onAnalyze} disabled={analyzing} className="btn-primary text-xs py-1.5">
          {analyzing ? 'Analyzing…' : 'Run Analysis'}
        </button>
      </div>

      {/* Top Picks */}
      {qualifiedRecs.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">AI Top Picks</div>
          <div className="space-y-2">
            {qualifiedRecs.map((rec, i) => (
              <TopPickCard key={rec.id} recommendation={rec} game={game} gameDate={gameDate} rank={i + 1} />
            ))}
          </div>
        </div>
      )}

      {/* Market rows */}
      {marketRows.length > 0 ? (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">All Markets</div>
          <div className="bg-surface-2 rounded-lg border border-surface-4 overflow-hidden divide-y divide-surface-4">
            {marketRows.map((row) => (
              <MarketRow
                key={row.market}
                {...row}
                game={game}
                gameDate={gameDate}
                recommendation={recByMarket.get(row.market) ?? allQualifiedRecByMarket.get(row.market)}
                isBetPlaced={placedMarkets.has(row.market)}
                isQualifiedGame={qualifiedRecs.length > 0 || placedMarkets.size > 0}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-600 italic text-center py-4">No odds available for this game</div>
      )}

      {/* Other recs */}
      {otherRecs.length > 0 && qualifiedRecs.length === 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">Other Analyzed Markets</div>
          <div className="space-y-1">
            {otherRecs.map((rec) => (
              <TopPickCard key={rec.id} recommendation={rec} game={game} gameDate={gameDate} />
            ))}
          </div>
        </div>
      )}

      {recommendations.length === 0 && !analyzing && (
        <div className="card p-6 text-center text-gray-500 text-sm">
          Click "Run Analysis" to generate betting recommendations for this game.
        </div>
      )}
    </div>
  );
}

// ── Bets tab ──────────────────────────────────────────────────────────────────

function BetsTab({
  placedBets,
  game,
  liveData,
  isLive,
  isFinal,
  awayScore,
  homeScore,
}: {
  placedBets: Bet[];
  game: MLBGame;
  liveData?: LiveGameData;
  isLive: boolean;
  isFinal: boolean;
  awayScore?: number;
  homeScore?: number;
}) {
  if (placedBets.length === 0) {
    return <div className="text-sm text-gray-600 italic text-center py-8">No bets placed on this game</div>;
  }

  const awayTeam = game.awayTeam.abbreviation;
  const homeTeam = game.homeTeam.abbreviation;

  let scoreLabel: string | null = null;
  if ((isLive || isFinal) && awayScore != null && homeScore != null) {
    if (awayScore === homeScore) scoreLabel = `Tied ${awayScore}-${homeScore}`;
    else if (awayScore > homeScore) scoreLabel = `${awayTeam} leads ${awayScore}-${homeScore}`;
    else scoreLabel = `${homeTeam} leads ${homeScore}-${awayScore}`;
  }

  return (
    <div className="space-y-3">
      {scoreLabel && (
        <div className="flex items-center gap-2 text-xs font-semibold py-1.5 px-3 rounded-md bg-surface-0 border border-surface-4 text-gray-200">
          {isLive && <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse" />}
          {scoreLabel}
        </div>
      )}
      <div className="space-y-2">
        {placedBets.map((bet) => {
          const isWon  = bet.result === 'won'  || bet.status === 'won';
          const isLost = bet.result === 'lost' || bet.status === 'lost';
          const isPush = bet.result === 'push' || bet.status === 'push';
          const isActive = bet.status === 'placed' || bet.status === 'live';

          let betLiveStatus: { label: string; cls: string; icon: React.ReactNode } | null = null;
          if ((isLive || isFinal) && awayScore != null && homeScore != null) {
            if (isFinal && !isActive) {
              if (isWon)  betLiveStatus = { label: 'Won',  cls: 'text-win',  icon: <TrendingUp className="w-3 h-3" /> };
              if (isLost) betLiveStatus = { label: 'Lost', cls: 'text-loss', icon: <TrendingDown className="w-3 h-3" /> };
              if (isPush) betLiveStatus = { label: 'Push', cls: 'text-warn', icon: <Minus className="w-3 h-3" /> };
            } else if (isActive && (bet.market === 'moneyline' || bet.market === 'runline')) {
              const sel = bet.selection.toLowerCase();
              const awL = awayTeam.toLowerCase();
              const hmL = homeTeam.toLowerCase();
              const awayWin = awayScore > homeScore;
              const homeWin = homeScore > awayScore;
              const tied = awayScore === homeScore;
              const onAway = sel.includes(awL);
              const onHome = sel.includes(hmL);
              if (tied) betLiveStatus = { label: 'Tied', cls: 'text-warn', icon: <Minus className="w-3 h-3" /> };
              else if ((onAway && awayWin) || (onHome && homeWin)) betLiveStatus = { label: 'Winning', cls: 'text-win', icon: <TrendingUp className="w-3 h-3" /> };
              else if ((onAway && homeWin) || (onHome && awayWin)) betLiveStatus = { label: 'Losing',  cls: 'text-loss', icon: <TrendingDown className="w-3 h-3" /> };
            }
          }

          return (
            <div
              key={bet.id}
              className={clsx(
                'rounded-lg border px-3 py-2.5 bg-surface-0',
                isWon ? 'border-win/30 bg-win/5' : isLost ? 'border-loss/20 bg-loss/5' : isPush ? 'border-gray-500/20' : 'border-surface-4',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{formatMarket(bet.market)}</span>
                    {isWon  && <span className="text-[10px] font-bold text-win">WON</span>}
                    {isLost && <span className="text-[10px] font-bold text-loss">LOST</span>}
                    {isPush && <span className="text-[10px] font-bold text-gray-400">PUSH</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold text-gray-200">{bet.selection}</span>
                    {isActive && betLiveStatus && (
                      <span className={clsx('flex items-center gap-0.5 text-[10px] font-semibold', betLiveStatus.cls)}>
                        {betLiveStatus.icon}
                        {betLiveStatus.label}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-mono font-semibold text-gray-300">{formatOdds(bet.odds)}</div>
                  <div className="text-[10px] text-gray-500">${bet.stakeAmount.toFixed(0)} stake</div>
                </div>
              </div>
              {bet.pnl != null && (
                <div className={clsx('text-xs font-mono font-bold mt-1', bet.pnl >= 0 ? 'text-win' : 'text-loss')}>
                  {bet.pnl >= 0 ? '+' : ''}${Math.abs(bet.pnl).toFixed(2)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Live feed tab ─────────────────────────────────────────────────────────────

function LiveFeedTab({ ctx }: { ctx: WorkspaceContext }) {
  const { game, liveData } = ctx;
  if (!liveData?.recentPlays?.length) {
    return <div className="flex items-center justify-center py-8 text-gray-600 text-sm">No live play data available</div>;
  }
  return (
    <SignalFeed
      plays={liveData.recentPlays}
      maxEvents={60}
      gameLabel={`${game.awayTeam.abbreviation} @ ${game.homeTeam.abbreviation}`}
    />
  );
}

// ── Stats tab ─────────────────────────────────────────────────────────────────

function StatsTab({ ctx }: { ctx: WorkspaceContext }) {
  const { game, liveData } = ctx;
  const awayBatting = liveData?.awayTeamStats;
  const homeBatting = liveData?.homeTeamStats;
  const awayPitcher = game.probablePitchers?.away;
  const homePitcher = game.probablePitchers?.home;

  return (
    <div className="space-y-5">
      {/* Team batting */}
      {(awayBatting || homeBatting) && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Team Batting</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-gray-600 uppercase tracking-wide border-b border-surface-4">
                  <th className="text-left py-1 pr-3">Team</th>
                  <th className="text-right py-1 px-2">AVG</th>
                  <th className="text-right py-1 px-2">OBP</th>
                  <th className="text-right py-1 px-2">SLG</th>
                  <th className="text-right py-1 px-2">OPS</th>
                  <th className="text-right py-1 px-2">K%</th>
                  <th className="text-right py-1 px-2">BB%</th>
                  {awayBatting?.woba != null && <th className="text-right py-1 px-2">wOBA</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-4">
                {[
                  { label: game.awayTeam.abbreviation, stats: awayBatting },
                  { label: game.homeTeam.abbreviation, stats: homeBatting },
                ].map(({ label, stats }) =>
                  stats ? (
                    <tr key={label} className="text-gray-300">
                      <td className="py-1.5 pr-3 font-semibold">{label}</td>
                      <td className="text-right py-1.5 px-2 font-mono">{stats.avg.toFixed(3)}</td>
                      <td className="text-right py-1.5 px-2 font-mono">{stats.obp.toFixed(3)}</td>
                      <td className="text-right py-1.5 px-2 font-mono">{stats.slg.toFixed(3)}</td>
                      <td className={clsx('text-right py-1.5 px-2 font-mono font-semibold', stats.ops >= 0.8 ? 'text-win' : stats.ops >= 0.7 ? 'text-warn' : 'text-gray-400')}>
                        {stats.ops.toFixed(3)}
                      </td>
                      <td className="text-right py-1.5 px-2 font-mono text-gray-400">{(stats.kPct * 100).toFixed(1)}%</td>
                      <td className="text-right py-1.5 px-2 font-mono text-gray-400">{(stats.bbPct * 100).toFixed(1)}%</td>
                      {awayBatting?.woba != null && <td className="text-right py-1.5 px-2 font-mono">{stats.woba?.toFixed(3) ?? '—'}</td>}
                    </tr>
                  ) : null,
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Starting pitchers */}
      {(awayPitcher || homePitcher) && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Starting Pitchers</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { team: game.awayTeam.abbreviation, pitcher: awayPitcher, livePc: liveData?.awayPitcher?.pitchCount },
              { team: game.homeTeam.abbreviation, pitcher: homePitcher, livePc: liveData?.homePitcher?.pitchCount },
            ].map(({ team, pitcher, livePc }) =>
              pitcher ? (
                <div key={team} className="bg-surface-0 rounded-lg border border-surface-4 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-[10px] font-bold text-gray-500 uppercase">{team} SP</span>
                      <div className="text-sm font-semibold text-gray-200 mt-0.5">{pitcher.fullName}</div>
                    </div>
                    {pitcher.handedness && (
                      <span className="text-[10px] bg-surface-3 border border-surface-4 px-1.5 py-0.5 rounded text-gray-500">{pitcher.handedness}HP</span>
                    )}
                  </div>
                  {pitcher.stats && (
                    <div className="grid grid-cols-3 gap-1.5">
                      <StatMini label="ERA"    value={pitcher.stats.era.toFixed(2)}   highlight={pitcher.stats.era <= 3.5 ? 'win' : pitcher.stats.era >= 5 ? 'loss' : 'neutral'} />
                      <StatMini label="WHIP"   value={pitcher.stats.whip.toFixed(2)}  highlight={pitcher.stats.whip <= 1.2 ? 'win' : pitcher.stats.whip >= 1.5 ? 'loss' : 'neutral'} />
                      <StatMini label="K/9"    value={pitcher.stats.kPer9.toFixed(1)} />
                      {pitcher.stats.fip     != null && <StatMini label="FIP"     value={pitcher.stats.fip.toFixed(2)} />}
                      {pitcher.stats.last5Era != null && <StatMini label="L5 ERA"  value={pitcher.stats.last5Era.toFixed(2)} highlight={pitcher.stats.last5Era <= 3.0 ? 'win' : pitcher.stats.last5Era >= 5 ? 'loss' : 'neutral'} />}
                      {livePc != null && <StatMini label="Pitches" value={String(livePc)} />}
                    </div>
                  )}
                </div>
              ) : null,
            )}
          </div>
        </div>
      )}

      {!awayBatting && !homeBatting && !awayPitcher && !homePitcher && (
        <div className="text-sm text-gray-600 italic text-center py-6">
          Stats not available yet — check back closer to game time
        </div>
      )}
    </div>
  );
}

// ── Context tab ───────────────────────────────────────────────────────────────

function ContextTab({ ctx }: { ctx: WorkspaceContext }) {
  const { weather, parkData, game } = ctx;
  const parkIsOutdoor = parkData?.isOutdoor ?? true;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div>
        {weather ? (
          <WeatherImpact weather={weather} isOutdoor={parkIsOutdoor} />
        ) : (
          <div className="card p-4 text-sm text-gray-600">Weather data unavailable.</div>
        )}
      </div>
      <div>
        <ParkFactors parkData={parkData} venueName={game.venue.name} />
      </div>
    </div>
  );
}

// ── Lines tab (MarketMicrostructure + live line movement) ─────────────────────

function LinesTab({ ctx }: { ctx: WorkspaceContext }) {
  const { odds, game } = ctx;
  const [history, setHistory] = useState<TimestampedOddsSnapshot[]>([]);
  const [signal, setSignal] = useState<LineMovementSignal | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    oddsApi
      .getLineMovement(game.awayTeam.name, game.homeTeam.name)
      .then(({ history: h, signal: s }) => { setHistory(h); setSignal(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [game.gamePk]);

  const awayAbbr = game.awayTeam.abbreviation;
  const homeAbbr = game.homeTeam.abbreviation;

  const chartData = [...history]
    .reverse()
    .filter((ts) => ts.snapshot.awayMoneyline != null || ts.snapshot.homeMoneyline != null)
    .map((ts) => ({
      label: new Date(ts.ingestedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      away: ts.snapshot.awayMoneyline ?? undefined,
      home: ts.snapshot.homeMoneyline ?? undefined,
      total: ts.snapshot.total ?? undefined,
    }));

  const opening = chartData[0];
  const current = chartData[chartData.length - 1];

  const strengthColor = signal
    ? signal.sharpSignalStrength >= 0.6 ? 'text-win' : signal.sharpSignalStrength >= 0.35 ? 'text-warn' : 'text-gray-500'
    : 'text-gray-500';

  return (
    <div className="space-y-5">
      {/* Live line movement */}
      {loading && <div className="text-xs text-gray-600 text-center py-2">Loading line movement…</div>}

      {!loading && signal && chartData.length >= 2 && (
        <div className="card p-4 space-y-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Line Movement</div>

          <div className="flex items-center gap-2">
            <Zap className={clsx('w-3.5 h-3.5', signal.sharpSignalStrength >= 0.35 ? 'text-warn' : 'text-gray-500')} />
            <span className={clsx('text-xs font-mono', strengthColor)}>
              {(signal.sharpSignalStrength * 100).toFixed(0)}% signal strength
            </span>
            {signal.steamMove.detected && (
              <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-warn/10 text-warn border border-warn/20">
                STEAM {signal.steamMove.magnitudePts}pt
              </span>
            )}
          </div>

          {signal.openingDrift.reliable && (opening?.away != null || opening?.home != null) && (
            <div className="flex flex-wrap gap-4 text-xs pb-3 border-b border-surface-4">
              {opening?.away != null && current?.away != null && (
                <div className="flex items-center gap-1.5 text-gray-500">
                  <span>{awayAbbr} ML:</span>
                  <span className="font-mono text-gray-400">{opening.away > 0 ? `+${opening.away}` : opening.away}</span>
                  <span className="text-gray-600">→</span>
                  <span className={clsx('font-mono font-semibold', current.away > opening.away ? 'text-win' : current.away < opening.away ? 'text-loss' : 'text-gray-300')}>
                    {current.away > 0 ? `+${current.away}` : current.away}
                  </span>
                  {signal.openingDrift.awayPts !== 0 && (
                    <span className={clsx('font-mono', signal.openingDrift.awayPts > 0 ? 'text-win' : 'text-loss')}>
                      ({signal.openingDrift.awayPts > 0 ? '+' : ''}{signal.openingDrift.awayPts})
                    </span>
                  )}
                </div>
              )}
              {opening?.home != null && current?.home != null && (
                <div className="flex items-center gap-1.5 text-gray-500">
                  <span>{homeAbbr} ML:</span>
                  <span className="font-mono text-gray-400">{opening.home > 0 ? `+${opening.home}` : opening.home}</span>
                  <span className="text-gray-600">→</span>
                  <span className={clsx('font-mono font-semibold', current.home > opening.home ? 'text-win' : current.home < opening.home ? 'text-loss' : 'text-gray-300')}>
                    {current.home > 0 ? `+${current.home}` : current.home}
                  </span>
                  {signal.openingDrift.homePts !== 0 && (
                    <span className={clsx('font-mono', signal.openingDrift.homePts > 0 ? 'text-win' : 'text-loss')}>
                      ({signal.openingDrift.homePts > 0 ? '+' : ''}{signal.openingDrift.homePts})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <OddsMovementChart data={chartData} height={140} showLines={['away', 'home']} />
          <p className="text-[10px] text-gray-500 leading-relaxed">{signal.summary}</p>
        </div>
      )}

      {!loading && (!signal || chartData.length < 2) && (
        <div className="text-xs text-gray-600 italic text-center py-2">
          Not enough snapshots yet — line movement analysis requires ≥2 ingestion cycles.
        </div>
      )}

      {/* MarketMicrostructure (existing detailed view) */}
      <MarketMicrostructure
        lineMovement={ctx.lineMovement ?? signal ?? undefined}
        odds={odds}
        awayTeamAbbr={awayAbbr}
        homeTeamAbbr={homeAbbr}
      />
    </div>
  );
}

// ── Evidence tab (enhanced AI analysis) ──────────────────────────────────────

function EvidenceTab({ ctx }: { ctx: WorkspaceContext }) {
  const { recommendations } = ctx;

  if (recommendations.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Brain className="w-8 h-8 text-gray-600 mx-auto mb-3" />
        <div className="text-sm text-gray-500 mb-2">No recommendations to analyze yet.</div>
        <div className="text-xs text-gray-600">Run analysis from the Markets tab to see the full AI reasoning breakdown here.</div>
      </div>
    );
  }

  const topRec = recommendations.reduce<BetRecommendation | undefined>((best, rec) => {
    if (!best) return rec;
    return rec.edge > best.edge ? rec : best;
  }, undefined);

  return (
    <div className="space-y-6">
      {/* Agent scores for top rec */}
      {topRec?.agentOpinions && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Agent Scores — {topRec.selection}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(topRec.agentOpinions).map(([name, opinion]) => {
              if (!opinion) return null;
              const score = opinion.score ?? 0;
              const scoreCls = score >= 0.65 ? 'text-win' : score <= 0.35 ? 'text-loss' : 'text-warn';
              const pct = (score * 100).toFixed(0);
              return (
                <div key={name} className="bg-surface-0 rounded-lg border border-surface-4 p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] text-gray-500 capitalize font-medium">{name}</span>
                    <span className={clsx('text-xs font-mono font-bold', scoreCls)}>{pct}%</span>
                  </div>
                  <div className="h-1 bg-surface-3 rounded-full overflow-hidden mb-1.5">
                    <div className={clsx('h-full rounded-full transition-all', score >= 0.65 ? 'bg-win' : score <= 0.35 ? 'bg-loss' : 'bg-warn')} style={{ width: `${pct}%` }} />
                  </div>
                  {opinion.reasoning?.[0] && (
                    <p className="text-[9px] text-gray-600 leading-relaxed line-clamp-2">{opinion.reasoning[0].text}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full evidence per recommendation */}
      {recommendations.map((rec) => (
        <div key={rec.id} className="card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-100">{rec.selection}</div>
              <div className="text-xs text-gray-500">{rec.market} · {rec.decisionStatus.replace(/_/g, ' ')}</div>
            </div>
            <div className="text-right">
              <div className={clsx('text-sm font-mono font-bold', rec.edge >= 0.07 ? 'text-win' : rec.edge >= 0.03 ? 'text-warn' : 'text-gray-400')}>
                +{(rec.edge * 100).toFixed(1)}% edge
              </div>
              <div className="text-xs text-gray-500">{(rec.confidence * 100).toFixed(0)}% confidence</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {rec.reasonsFor.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-win/80 mb-2">Reasons For</div>
                <div className="space-y-1.5">
                  {rec.reasonsFor.map((r, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-gray-400">
                      <TrendingUp className="w-3 h-3 text-win flex-shrink-0 mt-px" />
                      <span>{r.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {rec.reasonsAgainst.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-loss/80 mb-2">Reasons Against</div>
                <div className="space-y-1.5">
                  {rec.reasonsAgainst.map((r, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-gray-500">
                      <TrendingDown className="w-3 h-3 text-loss flex-shrink-0 mt-px" />
                      <span>{r.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Risk tab ──────────────────────────────────────────────────────────────────

function RiskTab({ ctx }: { ctx: WorkspaceContext }) {
  const { recommendations, dataQuality } = ctx;
  const topRec = recommendations.reduce<BetRecommendation | undefined>((best, rec) => {
    if (!best) return rec;
    return rec.edge > best.edge ? rec : best;
  }, undefined);

  return (
    <div className="space-y-4">
      {topRec ? (
        <>
          {/* Validation flags */}
          {topRec.trustReasonCodes && topRec.trustReasonCodes.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-warn mb-2">Validation Flags</div>
              <div className="space-y-1">
                {topRec.trustReasonCodes.map((code, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-warn/80 bg-warn/5 border border-warn/20 rounded px-2.5 py-1.5">
                    <span>⚠</span><span>{code}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key risks */}
          {topRec.keyRisks && topRec.keyRisks.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Key Risks</div>
              <div className="space-y-1">
                {topRec.keyRisks.map((risk, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-500 bg-surface-0 border border-surface-4 rounded px-2.5 py-1.5">
                    <span className="flex-shrink-0">·</span><span>{risk}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data quality from rec */}
          {topRec.dataQuality && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Data Quality</div>
              <div className="grid grid-cols-3 gap-2">
                <StatMini label="Reliability" value={`${Math.round(topRec.dataQuality.reliabilityScore * 100)}%`}  highlight={topRec.dataQuality.reliabilityScore >= 0.75 ? 'win' : topRec.dataQuality.reliabilityScore >= 0.5 ? 'neutral' : 'loss'} />
                <StatMini label="Freshness"   value={`${Math.round(topRec.dataQuality.freshnessScore * 100)}%`}    highlight={topRec.dataQuality.freshnessScore >= 0.75 ? 'win' : topRec.dataQuality.freshnessScore >= 0.5 ? 'neutral' : 'loss'} />
                <StatMini label="Complete"    value={`${Math.round(topRec.dataQuality.completenessScore * 100)}%`} highlight={topRec.dataQuality.completenessScore >= 0.75 ? 'win' : topRec.dataQuality.completenessScore >= 0.5 ? 'neutral' : 'loss'} />
              </div>
              {topRec.dataQuality.warnings.length > 0 && (
                <div className="mt-2 space-y-1">
                  {topRec.dataQuality.warnings.slice(0, 3).map((w, i) => (
                    <div key={i} className={clsx('text-[10px] px-2 py-1 rounded border',
                      w.severity === 'critical' ? 'text-loss bg-loss/5 border-loss/20' :
                      w.severity === 'warning'  ? 'text-warn bg-warn/5 border-warn/20' :
                      'text-gray-500 bg-surface-0 border-surface-4',
                    )}>
                      {w.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Recommended stake */}
          {topRec.recommendedStake && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Recommended Stake</div>
              <div className="bg-surface-0 border border-surface-4 rounded-lg p-3 text-xs">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <div className="text-gray-500">Suggested amount</div>
                  <div className="font-mono font-semibold text-gray-200 text-right">${Math.round(topRec.recommendedStake.recommendedAmount)}</div>
                  <div className="text-gray-500">Kelly fraction</div>
                  <div className="font-mono text-gray-400 text-right">{(topRec.recommendedStake.kellyFraction * 100).toFixed(1)}%</div>
                  <div className="text-gray-500">Max stake</div>
                  <div className="font-mono text-gray-400 text-right">${Math.round(topRec.recommendedStake.maxAmount)}</div>
                </div>
                <p className="text-[10px] text-gray-600 mt-2">{topRec.recommendedStake.reasoning}</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-gray-600 italic text-center py-4">No risk analysis available — run analysis first</div>
      )}

      {/* Global data quality badge */}
      <DataQualityBadge quality={dataQuality} showDetails />
    </div>
  );
}

// ── Signals tab ───────────────────────────────────────────────────────────────

function SignalsTab({
  ctx,
  lineSignal,
}: {
  ctx: WorkspaceContext;
  lineSignal: LineMovementSignal | null;
}) {
  const { recommendations, odds } = ctx;

  const steamSignal = lineSignal?.steamMove.detected ? lineSignal.steamMove : null;

  const marketSignals = recommendations.flatMap((rec) =>
    (rec.agentOpinions?.market?.reasoning ?? []).map((r) => ({
      ...r,
      market: rec.market,
      selection: rec.selection,
    })),
  );

  return (
    <div className="space-y-4">
      {steamSignal && (
        <div className="bg-warn/10 border border-warn/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-warn" />
            <span className="text-xs font-bold text-warn uppercase tracking-wide">Steam Move Detected</span>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-[11px] font-mono">
            <span className="text-gray-500">Direction: <span className="text-warn">{steamSignal.direction}</span></span>
            <span className="text-gray-500">Magnitude: <span className="text-warn">{steamSignal.magnitudePts}pts</span></span>
            <span className="text-gray-500">Window: <span className="text-gray-300">{steamSignal.windowMinutes}min</span></span>
          </div>
        </div>
      )}

      {/* Odds source */}
      {odds && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Odds Source</div>
          <div className="bg-surface-0 border border-surface-4 rounded-lg p-3 text-xs">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
              <span className="text-gray-500">Source</span>
              <span className="text-gray-300 font-mono">{odds.source}</span>
              {'reliability' in odds && odds.reliability != null && (
                <>
                  <span className="text-gray-500">Reliability</span>
                  <span className={clsx('font-mono', (odds as { reliability: number }).reliability >= 0.8 ? 'text-win' : 'text-warn')}>
                    {((odds as { reliability: number }).reliability * 100).toFixed(0)}%
                  </span>
                </>
              )}
              {'isStale' in odds && (odds as { isStale?: boolean }).isStale && (
                <span className="col-span-2 text-warn text-[10px]">⚠ Odds may be stale</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Market signals */}
      {marketSignals.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Market Signals</div>
          <div className="space-y-1.5">
            {marketSignals.slice(0, 8).map((sig, i) => (
              <div
                key={i}
                className={clsx(
                  'flex items-start gap-2 text-[11px] px-2.5 py-1.5 rounded border',
                  sig.direction === 'positive' ? 'text-win/80 bg-win/5 border-win/15' :
                  sig.direction === 'negative' ? 'text-loss/80 bg-loss/5 border-loss/15' :
                  'text-gray-500 bg-surface-0 border-surface-4',
                )}
              >
                <TrendingUp className={clsx('w-3 h-3 flex-shrink-0 mt-px', sig.direction === 'positive' ? 'text-win' : sig.direction === 'negative' ? 'text-loss rotate-180' : 'text-gray-600')} />
                <span>{sig.text}</span>
                {sig.dataPoint && <span className="ml-auto font-mono text-[9px] text-gray-600 flex-shrink-0">{sig.dataPoint}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!steamSignal && marketSignals.length === 0 && (
        <div className="text-sm text-gray-600 italic text-center py-6">No market signals detected</div>
      )}
    </div>
  );
}

// ── Simulation tab ────────────────────────────────────────────────────────────

function SimulationTab({ ctx }: { ctx: WorkspaceContext }) {
  const { recommendations, liveData } = ctx;

  if (recommendations.length === 0) {
    return (
      <div className="card p-8 text-center">
        <FlaskConical className="w-8 h-8 text-gray-600 mx-auto mb-3" />
        <div className="text-sm text-gray-500 mb-2">No simulation data available yet.</div>
        <div className="text-xs text-gray-600">Run analysis from the Markets tab to generate confidence evolution and outcome scenarios.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {recommendations.map((rec) => {
        const bullishFactors = rec.reasonsFor.filter((r) => r.weight === 'high' || r.weight === 'medium');
        const bearishFactors = rec.reasonsAgainst.filter((r) => r.weight === 'high' || r.weight === 'medium');
        const agentScores = Object.values(rec.agentOpinions).map((o) => o.score);
        const baseConf = agentScores.length > 0 ? agentScores.reduce((s, v) => s + v, 0) / agentScores.length : rec.confidence * 0.85;

        const syntheticHistory = [
          { timestamp: new Date(Date.now() - 90 * 60000).toISOString(), confidence: Math.max(0.3, baseConf - 0.12), edge: rec.edge * 0.6 },
          { timestamp: new Date(Date.now() - 60 * 60000).toISOString(), confidence: Math.max(0.35, baseConf - 0.06), edge: rec.edge * 0.8 },
          { timestamp: new Date(Date.now() - 30 * 60000).toISOString(), confidence: Math.max(0.4, baseConf), edge: rec.edge * 0.9, event: 'Market line movement detected' },
          { timestamp: new Date(Date.now() - 10 * 60000).toISOString(), confidence: rec.confidence, edge: rec.edge, event: rec.reasonsFor[0]?.text },
        ].filter((s) => s.confidence > 0);

        const winProb = rec.confidence;
        const loseProb = 1 - winProb;
        const awayWinProb = liveData?.winProbability?.away ?? 0.48;
        const homeWinProb = liveData?.winProbability?.home ?? (1 - awayWinProb);

        return (
          <div key={rec.id} className="space-y-4">
            <div className="flex items-center gap-2 pb-1 border-b border-surface-4">
              <Zap className="w-3.5 h-3.5 text-brand" />
              <span className="text-sm font-semibold text-gray-100">{rec.selection}</span>
              <span className="text-xs text-gray-500">{rec.market}</span>
            </div>

            <ConfidenceEvolution recommendation={rec} history={syntheticHistory} />

            <div className="card p-4 space-y-3">
              <div className="text-[10px] text-gray-600 uppercase tracking-wider">Outcome Probability</div>
              <div className="space-y-2.5">
                {[
                  { label: 'Bet wins', pct: winProb, cls: 'text-win', bar: 'bg-win' },
                  { label: 'Bet loses', pct: loseProb, cls: 'text-loss', bar: 'bg-loss' },
                ].map(({ label, pct, cls, bar }) => (
                  <div key={label} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">{label}</span>
                      <span className={clsx('font-mono', cls)}>{(pct * 100).toFixed(1)}%</span>
                    </div>
                    <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                      <div className={clsx('h-full rounded-full transition-all duration-700', bar)} style={{ width: `${pct * 100}%` }} />
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t border-surface-4 grid grid-cols-2 gap-2">
                  {[
                    { label: 'Away win', pct: awayWinProb },
                    { label: 'Home win', pct: homeWinProb },
                  ].map(({ label, pct }) => (
                    <div key={label} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-mono text-gray-400">{(pct * 100).toFixed(0)}%</span>
                      </div>
                      <div className="h-1 bg-surface-3 rounded-full overflow-hidden">
                        <div className="h-full bg-brand/60 rounded-full" style={{ width: `${pct * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card p-4 space-y-3">
              <div className="text-[10px] text-gray-600 uppercase tracking-wider">Scenario Matrix</div>
              <div className="grid grid-cols-2 gap-3">
                {bullishFactors.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] text-win uppercase tracking-wide font-bold">Bull case</div>
                    {bullishFactors.slice(0, 3).map((r, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs">
                        <span className="text-win mt-0.5 flex-shrink-0">+</span>
                        <span className="text-gray-400 leading-snug">{r.text}</span>
                      </div>
                    ))}
                  </div>
                )}
                {bearishFactors.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] text-loss uppercase tracking-wide font-bold">Bear case</div>
                    {bearishFactors.slice(0, 3).map((r, i) => (
                      <div key={i} className="flex items-start gap-1.5 text-xs">
                        <span className="text-loss mt-0.5 flex-shrink-0">−</span>
                        <span className="text-gray-400 leading-snug">{r.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="pt-3 border-t border-surface-4">
                <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-2">Agent consensus</div>
                <div className="grid grid-cols-4 gap-1.5">
                  {Object.entries(rec.agentOpinions).slice(0, 8).map(([name, opinion]) => (
                    <div key={name} className={clsx('rounded px-1.5 py-1 text-center border',
                      opinion.score >= 0.65 ? 'bg-win/10 border-win/20' :
                      opinion.score <= 0.4  ? 'bg-loss/10 border-loss/20' :
                      'bg-surface-3 border-surface-4',
                    )}>
                      <div className="text-[8px] uppercase tracking-wide text-gray-600 truncate">{name}</div>
                      <div className={clsx('text-[11px] font-mono font-bold', opinion.score >= 0.65 ? 'text-win' : opinion.score <= 0.4 ? 'text-loss' : 'text-gray-400')}>
                        {(opinion.score * 100).toFixed(0)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main workspace ────────────────────────────────────────────────────────────

export function ExpandedGameWorkspace(ctx: WorkspaceContext) {
  const { game, liveData, recommendations } = ctx;
  const { bets } = useAppStore();

  const [activeTab, setActiveTab] = useState<WorkspaceTab>('overview');
  const [lineSignal, setLineSignal] = useState<LineMovementSignal | null>(null);

  // Fetch line movement once for Signals tab (Lines tab fetches its own)
  useEffect(() => {
    oddsApi
      .getLineMovement(game.awayTeam.name, game.homeTeam.name)
      .then(({ signal }) => setLineSignal(signal))
      .catch(() => {});
  }, [game.gamePk]);

  const displayGame = liveData ?? game;
  const detailedState = displayGame.status.detailedState ?? '';
  const isNonStandard = /postponed|cancelled|canceled|suspended/i.test(detailedState);
  const isLive  = displayGame.status.abstractGameState === 'Live'  && !isNonStandard;
  const isFinal = displayGame.status.abstractGameState === 'Final' && !isNonStandard;

  const awayScore = displayGame.linescore?.awayScore ?? displayGame.awayScore;
  const homeScore = displayGame.linescore?.homeScore ?? displayGame.homeScore;

  // Placed bets for this game
  const placedBets = bets.filter(
    (b) => b.gamePk === game.gamePk && b.status !== 'candidate' && b.status !== 'recommended',
  );

  // Best recommendation for decision chip
  const bestRec = recommendations.reduce<BetRecommendation | undefined>((best, rec) => {
    if (!best) return rec;
    const priority: Record<string, number> = { STRONG_BET: 0, QUALIFIED_BET: 1, LEAN: 2, NO_BET: 3, INVALID: 4 };
    const bp = priority[best.decisionStatus] ?? 99;
    const rp = priority[rec.decisionStatus] ?? 99;
    return rp < bp || (rp === bp && rec.edge > best.edge) ? rec : best;
  }, undefined);

  const steamSignal = lineSignal?.steamMove.detected ? lineSignal.steamMove : null;

  const hasLiveData = isLive || (liveData?.recentPlays?.length ?? 0) > 0;

  const visibleTabs = ALL_TABS.filter((t) => t.id !== 'live' || hasLiveData);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-center px-4 border-b border-surface-4 bg-surface-1 flex-shrink-0 overflow-x-auto scrollbar-hide">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0',
              activeTab === id
                ? 'border-brand text-brand'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-surface-4',
            )}
          >
            <Icon className="w-3 h-3" />
            {label}
            {id === 'markets' && recommendations.length > 0 && (
              <span className="ml-0.5 text-[9px] bg-brand/20 text-brand-light rounded-full px-1 py-0.5">
                {recommendations.filter((r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET').length || recommendations.length}
              </span>
            )}
            {id === 'bets' && placedBets.length > 0 && (
              <span className="ml-0.5 text-[9px] bg-brand/20 text-brand-light rounded-full px-1 py-0.5">
                {placedBets.length}
              </span>
            )}
            {id === 'live' && isLive && (
              <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse" />
            )}
            {id === 'signals' && steamSignal && (
              <span className="w-1.5 h-1.5 rounded-full bg-warn animate-pulse" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <OverviewTab ctx={ctx} bestRec={bestRec} steamSignal={steamSignal} placedBetsCount={placedBets.length} />
        )}
        {activeTab === 'markets' && (
          <MarketsTab ctx={ctx} placedBets={placedBets} />
        )}
        {activeTab === 'bets' && (
          <BetsTab
            placedBets={placedBets}
            game={game}
            liveData={liveData}
            isLive={isLive}
            isFinal={isFinal}
            awayScore={isLive || isFinal ? awayScore : undefined}
            homeScore={isLive || isFinal ? homeScore : undefined}
          />
        )}
        {activeTab === 'live' && <LiveFeedTab ctx={ctx} />}
        {activeTab === 'stats' && <StatsTab ctx={ctx} />}
        {activeTab === 'matchup' && (
          <MatchupWorkspace
            awayPitcher={ctx.game.probablePitchers?.away}
            homePitcher={ctx.game.probablePitchers?.home}
            awayBullpen={ctx.awayBullpen}
            homeBullpen={ctx.homeBullpen}
            awayLineup={ctx.liveData?.awayLineup}
            homeLineup={ctx.liveData?.homeLineup}
            awayTeamAbbr={ctx.game.awayTeam.abbreviation}
            homeTeamAbbr={ctx.game.homeTeam.abbreviation}
          />
        )}
        {activeTab === 'context' && <ContextTab ctx={ctx} />}
        {activeTab === 'lines' && <LinesTab ctx={ctx} />}
        {activeTab === 'evidence' && <EvidenceTab ctx={ctx} />}
        {activeTab === 'risk' && <RiskTab ctx={ctx} />}
        {activeTab === 'signals' && <SignalsTab ctx={ctx} lineSignal={lineSignal} />}
        {activeTab === 'simulation' && <SimulationTab ctx={ctx} />}
      </div>
    </div>
  );
}
